import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { CalendarClock, Ban } from "lucide-react";
import { toast } from "sonner";

const EDITABLE = ["open", "pending", "accepted"];

export function ScheduleActions({ job, role, onChanged }) {
  const [date, setDate] = useState(job.date);
  const [time, setTime] = useState(job.time);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!EDITABLE.includes(job.status)) return null;
  const isPM = role === "pm";
  const cleanerCanWithdraw = role === "cleaner" && ["pending", "accepted"].includes(job.status);
  if (!isPM && !cleanerCanWithdraw) return null;

  const run = async (fn, msg) => {
    setBusy(true);
    try { await fn(); toast.success(msg); onChanged?.(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <div className="flex gap-2 pt-3 mt-3 border-t border-border" data-testid="schedule-actions">
      {isPM && (
        <Dialog>
          <DialogTrigger asChild>
            <Button variant="outline" size="sm" className="flex-1" data-testid="reschedule-open"><CalendarClock className="w-4 h-4 mr-1" /> Reschedule</Button>
          </DialogTrigger>
          <DialogContent className="bg-white">
            <DialogHeader><DialogTitle className="font-heading">Reschedule job</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Date</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="reschedule-date" /></div>
              <div><Label className="uppercase text-xs tracking-[0.2em] mb-1.5 block">Time</Label><Input type="time" value={time} onChange={(e) => setTime(e.target.value)} data-testid="reschedule-time" /></div>
            </div>
            {job.cleaner && <p className="text-xs text-muted-foreground">{job.cleaner.name}'s availability will be re-checked and they'll be notified.</p>}
            <DialogFooter>
              <Button className="bg-ocean hover:bg-ocean/90" disabled={busy || !date || !time}
                onClick={() => run(() => api.put(`/jobs/${job.id}/reschedule`, { date, time }), "Job rescheduled")} data-testid="reschedule-submit">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
      <Dialog>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="flex-1 text-signal border-signal hover:bg-signal hover:text-white" data-testid="cancel-open">
            <Ban className="w-4 h-4 mr-1" /> {isPM ? "Cancel Job" : "Withdraw"}
          </Button>
        </DialogTrigger>
        <DialogContent className="bg-white">
          <DialogHeader><DialogTitle className="font-heading">{isPM ? "Cancel this job?" : "Withdraw from this job?"}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            {isPM ? "The job will be marked cancelled and any assigned cleaner or bidders will be notified. This can't be undone." : "The job will go back to the marketplace and the host will be notified."}
          </p>
          <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (optional)" data-testid="cancel-reason" />
          <DialogFooter>
            <Button className="bg-signal hover:bg-signal/90" disabled={busy}
              onClick={() => run(() => api.post(`/jobs/${job.id}/cancel`, { reason }), isPM ? "Job cancelled" : "You've withdrawn from the job")} data-testid="cancel-submit">
              Confirm
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
